import {
  Media,
  MediaType,
  formatDateToMMDDYYYY,
  formatShortDateOrSameDay,
} from '@gripp/shared-logic';
import { ReactNode } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { Divider } from '../crop-circle/Components/Divider';
import { StatusTag } from '../crop-circle/Components/Tag/StatusTag';
import { tokens } from '../crop-circle/tokens';
import { CategoryBadge } from './categoryBadge';
import { RenderMediaItem } from './renderMediaItem';
import { useTypedMediaItem } from './useTypedMediaItem';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

const MAX_VISIBLE_LABELS = 2;

type MediaListItemProps = {
  media: Media;
  invalidateQuery?: () => void;
  menu?: ReactNode; // Optional menu component for entity-specific actions
  useNewCropCircle?: boolean;
  onClickMedia?: (media: Media) => void;
};

/**
 * Shared component for displaying a media item in a list
 * Used by Product and Asset file lists
 * Handles images, documents, and links with proper click handlers
 */
export const MediaListItem = ({
  media,
  invalidateQuery,
  menu,
  useNewCropCircle,
  onClickMedia,
}: MediaListItemProps) => {
  const { typedMediaItem } = useTypedMediaItem(media);

  if (!useNewCropCircle) {
    return (
      <View style={styles.container}>
        <View style={styles.fileContainer}>
          {typedMediaItem && (
            <RenderMediaItem
              mediaItem={typedMediaItem}
              displayWidth={80}
              hasLightbox={media.type === MediaType.Image}
            />
          )}
          <View style={styles.textContainer}>
            <Text style={styles.name} numberOfLines={1} ellipsizeMode="tail">
              {media.name}
            </Text>
            <Text style={styles.subLabel}>
              {formatDateToMMDDYYYY(media.createdAt)} •{' '}
              {media.createdBy?.name.first} {media.createdBy?.name.last}
            </Text>
            {media.categories && media.categories.length > 0 && (
              <View style={styles.categoryContainer}>
                {media.categories.map((category) => (
                  <CategoryBadge key={category.id} category={category} />
                ))}
              </View>
            )}
          </View>
        </View>
        {menu}
      </View>
    );
  }

  return (
    <>
      <View style={styles.containerCropCircle}>
        <View style={styles.fileContainerCropCircle}>
          {typedMediaItem && (
            <RenderMediaItem
              mediaItem={typedMediaItem}
              displayWidth={98}
              hasLightbox={media.type === MediaType.Image}
              onPress={() => onClickMedia?.(media)}
            />
          )}
          <TouchableOpacity
            style={styles.textContainerCropCircle}
            onPress={() => onClickMedia?.(media)}
          >
            <View style={styles.topRowCropCircle}>
              <Text
                style={[tokens.typography.label.large, styles.nameCropCircle]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {media.name}
              </Text>
              {media.createdAt && (
                <Text
                  style={[
                    tokens.typography.label.medium,
                    styles.dateCropCircle,
                  ]}
                >
                  {formatShortDateOrSameDay(media.createdAt)}
                </Text>
              )}
            </View>
            {media.categories && media.categories.length > 0 && (
              <View style={styles.categoryContainerCropCircle}>
                {media.categories
                  .slice(0, MAX_VISIBLE_LABELS)
                  .map((category) => (
                    <StatusTag
                      key={category.id}
                      variant="neutral"
                      text={category.name}
                    />
                  ))}
                {media.categories.length > MAX_VISIBLE_LABELS && (
                  <StatusTag
                    variant="neutral"
                    text={`+${media.categories.length - MAX_VISIBLE_LABELS}`}
                  />
                )}
              </View>
            )}
          </TouchableOpacity>
        </View>
        {menu}
      </View>
      <Divider type="horizontal" />
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderColor: role.borderStrong,
    borderWidth: 1,
    backgroundColor: role.surface,
    marginBottom: 12,
  },
  fileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    flex: 1,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  name: {
    fontSize: 16,
    color: role.textPrimary,
    fontWeight: '600',
  },
  subLabel: {
    fontSize: 14,
    color: role.textSecondary,
    fontWeight: '500',
    marginTop: 12,
  },
  categoryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 8,
  },
  containerCropCircle: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    padding: 16,
    backgroundColor: role.surface,
  },
  fileContainerCropCircle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    flex: 1,
  },
  textContainerCropCircle: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
    justifyContent: 'space-between',
    height: 98,
    flexDirection: 'column',
  },
  topRowCropCircle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  nameCropCircle: {
    flex: 1,
    color: role.textStrong,
  },
  categoryContainerCropCircle: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 8,
    marginBottom: 24,
  },
  dateCropCircle: {
    color: role.textSoft,
  },
}));
